// src/pages/ActivarConvenios.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaHandshake, FaCheckCircle, FaTimesCircle, FaDownload } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, addDoc, Timestamp, getDocs, where } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { toCsv, downloadCsv } from '../utils/exportCsv';
import './ActivarConvenios.css';
import { calcularEstadoMoroso, useConfiguracionFinanciera } from '../utils/morosoUtils';

const pickFirstString = (values = []) => {
    for (const value of values) {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.length > 0) return trimmed;
        }
    }
    return '';
};

const getResidentName = (casa = {}) => pickFirstString([
    casa.nombre_residente,
    casa.nombre,
    casa.nombreTitular,
    casa.titular,
    casa.propietario,
    casa.arrendador,
    casa.nombrePropietario,
    casa.nombreCompleto,
]);

const ActivarConvenios = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    // Obtener configuración financiera para cálculo de morosidad
    const { cuotaMensual, mesesPermitidos, loading: configLoading } = useConfiguracionFinanciera(fraccionamientoId);

    const [casas, setCasas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmationModal, setConfirmationModal] = useState({
        open: false,
        title: '',
        message: '',
        type: 'info',
        onAction: null,
    });
    const [processingId, setProcessingId] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loadingHistorical, setLoadingHistorical] = useState(false);

    // Obtener solo casas con morosidad
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const q = query(casasRef, orderBy('numero'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const casasList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar: solo casas con morosidad (nueva regla: adeudo >= umbral) y excluir caseta/guardia
            const casasMorosas = casasList.filter(casa => {
                // Excluir caseta/guardia
                if (casa.perfil) {
                    const p = String(casa.perfil).toLowerCase().trim();
                    if (p === 'caseta' || p === 'guardia') return false;
                }

                // Nueva regla: moroso solo si adeudo >= (cuotaMensual × mesesPermitidos)
                const adeudo = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                return calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
            });

            setCasas(casasMorosas);
            setLoading(false);
        }, (error) => {
            console.error('Error en listener de casas:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId, cuotaMensual, mesesPermitidos]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
    };

    const handleToggleConvenio = async (casa) => {
        const convenioActivo = casa.convenioActivo === true;
        const action = convenioActivo ? 'desactivar' : 'activar';

        setConfirmationModal({
            open: true,
            title: convenioActivo ? 'Desactivar Convenio' : 'Activar Convenio',
            message: convenioActivo
                ? `¿Estás seguro de que deseas desactivar el convenio para la casa ${casa.numero}? El residente volverá a estar sujeto a intereses moratorios.`
                : `¿Estás seguro de que deseas activar el convenio para la casa ${casa.numero}? El residente quedará exento de intereses moratorios durante el periodo de activación.`,
            type: convenioActivo ? 'warning' : 'info',
            onAction: async () => {
                setProcessingId(casa.id);
                try {
                    const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', casa.id);
                    const updateData = {
                        convenioActivo: !convenioActivo,
                        convenioFechaActualizacion: serverTimestamp(),
                    };

                    // Si se activa, guardar la fecha de activación y el adeudo inicial
                    if (!convenioActivo) {
                        const adeudoActual = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                        updateData.convenioFechaActivacion = serverTimestamp();
                        updateData.convenioAdeudoInicial = adeudoActual;
                    } else {
                        // Si se desactiva, guardar la fecha de desactivación y el adeudo final
                        const adeudoActual = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                        updateData.convenioFechaDesactivacion = serverTimestamp();
                        updateData.convenioAdeudoFinal = adeudoActual;
                    }

                    await updateDoc(casaRef, updateData);

                    setConfirmationModal({
                        open: true,
                        title: convenioActivo ? 'Convenio Desactivado' : 'Convenio Activado',
                        message: convenioActivo
                            ? `El convenio para la casa ${casa.numero} ha sido desactivado exitosamente.`
                            : `El convenio para la casa ${casa.numero} ha sido activado exitosamente. El residente quedará exento de intereses durante el periodo de activación.`,
                        type: 'success',
                        onAction: null,
                    });
                } catch (error) {
                    console.error('Error al actualizar convenio:', error);
                    setConfirmationModal({
                        open: true,
                        title: 'Error',
                        message: 'Hubo un error al procesar la solicitud. Intenta nuevamente.',
                        type: 'error',
                        onAction: null,
                    });
                } finally {
                    setProcessingId(null);
                }
            },
        });
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            let date;
            if (timestamp.toDate) {
                date = timestamp.toDate();
            } else if (timestamp instanceof Date) {
                date = timestamp;
            } else if (typeof timestamp === 'string') {
                date = new Date(timestamp);
            } else {
                return 'N/A';
            }
            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    const loadHistoricalReport = async (month, year) => {
        if (!fraccionamientoId) return null;

        setLoadingHistorical(true);
        try {
            // Crear rango de fechas para el mes seleccionado
            const startDate = new Date(year, month - 1, 1);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(year, month, 0, 23, 59, 59);
            endDate.setHours(23, 59, 59, 999);

            const startTimestamp = Timestamp.fromDate(startDate);
            const endTimestamp = Timestamp.fromDate(endDate);

            // Buscar reportes en el rango de fechas
            // Nota: Si Firestore requiere índice compuesto, se creará automáticamente o mostrará error
            const reportesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'reportesConvenios');

            // Intentar consulta con orderBy primero (más eficiente)
            let q;
            try {
                q = query(
                    reportesRef,
                    where('fechaReporte', '>=', startTimestamp),
                    where('fechaReporte', '<=', endTimestamp),
                    orderBy('fechaReporte', 'desc')
                );
            } catch (indexError) {
                // Si falla por falta de índice, intentar sin orderBy y ordenar en memoria
                console.warn('Índice compuesto no encontrado, ordenando en memoria:', indexError);
                q = query(
                    reportesRef,
                    where('fechaReporte', '>=', startTimestamp),
                    where('fechaReporte', '<=', endTimestamp)
                );
            }

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                return null;
            }

            // Obtener el reporte más reciente del mes
            // Si no se pudo ordenar en la consulta, ordenar en memoria
            let reportes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Ordenar por fechaReporte descendente si no se ordenó en la consulta
            if (reportes.length > 1) {
                reportes.sort((a, b) => {
                    const fechaA = a.fechaReporte?.toDate?.() || new Date(0);
                    const fechaB = b.fechaReporte?.toDate?.() || new Date(0);
                    return fechaB - fechaA;
                });
            }

            return reportes[0];
        } catch (error) {
            console.error('Error cargando reporte histórico:', error);
            // Si el error es por índice faltante, mostrar mensaje más amigable
            if (error.message && error.message.includes('index')) {
                setConfirmationModal({
                    open: true,
                    title: 'Índice requerido',
                    message: 'Se requiere crear un índice en Firestore. Por favor, revisa la consola para más detalles.',
                    type: 'error',
                    onAction: null,
                });
            }
            return null;
        } finally {
            setLoadingHistorical(false);
        }
    };

    const handleExportReport = async (useHistorical = false) => {
        if (!fraccionamientoId) {
            setConfirmationModal({
                open: true,
                title: 'Error',
                message: 'No se pudo identificar el fraccionamiento.',
                type: 'error',
                onAction: null,
            });
            return;
        }

        setExporting(true);
        try {
            let reportRows = [];
            let filename = '';
            let reporteData = null;

            // Si se solicita reporte histórico, intentar cargarlo
            if (useHistorical) {
                const historicalReport = await loadHistoricalReport(selectedMonth, selectedYear);

                if (historicalReport && historicalReport.datos) {
                    reportRows = historicalReport.datos;
                    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('es-MX', { month: 'long' });
                    filename = `reporte_convenios_activos_${monthName}_${selectedYear}.csv`;
                    reporteData = historicalReport;
                } else {
                    setConfirmationModal({
                        open: true,
                        title: 'Sin datos históricos',
                        message: `No se encontró un reporte guardado para ${new Date(selectedYear, selectedMonth - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}.`,
                        type: 'info',
                        onAction: null,
                    });
                    setExporting(false);
                    return;
                }
            } else {
                // Filtrar solo casas con convenio activo (reporte actual)
                const casasConConvenio = casas.filter(casa => casa.convenioActivo === true);

                if (casasConConvenio.length === 0) {
                    setConfirmationModal({
                        open: true,
                        title: 'Sin datos',
                        message: 'No hay residentes con convenio activo en este momento.',
                        type: 'info',
                        onAction: null,
                    });
                    setExporting(false);
                    return;
                }

                // Preparar datos para el reporte actual
                reportRows = casasConConvenio.map(casa => {
                    const residentName = getResidentName(casa);
                    const adeudoActual = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                    const adeudoInicial = Number(casa?.convenioAdeudoInicial ?? 0);
                    const adeudoFinal = casa.convenioFechaDesactivacion
                        ? Number(casa?.convenioAdeudoFinal ?? 0)
                        : null;

                    return {
                        numeroCasa: String(casa.numero || 'N/A'),
                        nombreResidente: residentName || 'Residente no registrado',
                        fechaInicio: formatDate(casa.convenioFechaActivacion),
                        adeudoInicial: adeudoInicial,
                        fechaFin: casa.convenioFechaDesactivacion
                            ? formatDate(casa.convenioFechaDesactivacion)
                            : 'Activo',
                        adeudoFinal: casa.convenioFechaDesactivacion
                            ? (adeudoFinal !== null ? adeudoFinal : 'N/A')
                            : adeudoActual,
                        estado: casa.convenioFechaDesactivacion ? 'Finalizado' : 'Activo',
                    };
                });

                const fechaReporte = new Date().toISOString().slice(0, 10);
                filename = `reporte_convenios_activos_${fechaReporte}.csv`;
            }

            // Headers del CSV
            const headers = [
                { key: 'numeroCasa', label: 'Número de Casa' },
                { key: 'nombreResidente', label: 'Nombre del Residente' },
                { key: 'fechaInicio', label: 'Fecha Inicio Convenio' },
                { key: 'adeudoInicial', label: 'Adeudo Inicial' },
                { key: 'fechaFin', label: 'Fecha Fin Convenio' },
                { key: 'adeudoFinal', label: 'Adeudo Final' },
                { key: 'estado', label: 'Estado' },
            ];

            // Generar CSV
            const csv = toCsv(reportRows, headers);
            downloadCsv(csv, filename);

            // Guardar reporte en Firestore para historial (solo si es reporte actual, no histórico)
            if (!useHistorical) {
                try {
                    const nuevoReporteData = {
                        fechaGeneracion: serverTimestamp(),
                        fechaReporte: Timestamp.fromDate(new Date()),
                        tipo: 'convenios_activos',
                        totalRegistros: reportRows.length,
                        datos: reportRows.map(row => ({
                            numeroCasa: row.numeroCasa,
                            nombreResidente: row.nombreResidente,
                            fechaInicio: row.fechaInicio,
                            adeudoInicial: row.adeudoInicial,
                            fechaFin: row.fechaFin,
                            adeudoFinal: row.adeudoFinal,
                            estado: row.estado,
                        })),
                        generadoPor: userData?.email || userData?.nombre || 'Sistema',
                    };

                    await addDoc(
                        collection(db, 'fraccionamientos', fraccionamientoId, 'reportesConvenios'),
                        nuevoReporteData
                    );
                } catch (saveError) {
                    console.error('Error guardando reporte en historial:', saveError);
                    // No fallar la exportación si falla el guardado
                }
            }

            const mensaje = useHistorical
                ? `Se ha exportado exitosamente el reporte histórico de ${reportRows.length} convenio(s) para ${new Date(selectedYear, selectedMonth - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}.`
                : `Se ha exportado exitosamente el reporte de ${reportRows.length} convenio(s) activo(s). El reporte también se ha guardado en el historial.`;

            setConfirmationModal({
                open: true,
                title: 'Reporte Exportado',
                message: mensaje,
                type: 'success',
                onAction: null,
            });
        } catch (error) {
            console.error('Error al exportar reporte:', error);
            setConfirmationModal({
                open: true,
                title: 'Error',
                message: 'Hubo un error al generar el reporte. Intenta nuevamente.',
                type: 'error',
                onAction: null,
            });
        } finally {
            setExporting(false);
        }
    };

    const filteredCasas = casas.filter(casa => {
        const searchLower = searchTerm.toLowerCase();
        const numero = String(casa.numero || '').toLowerCase();
        const nombre = getResidentName(casa).toLowerCase();
        return numero.includes(searchLower) || nombre.includes(searchLower);
    });

    return (
        <div className="activar-convenios-page">
            <div className="activar-convenios-container">
                <header className="activar-convenios-header">
                    <div className="activar-convenios-header-icon">
                        <FaHandshake />
                    </div>
                    <div>
                        <h2>Activar Convenios con Residentes</h2>
                        <p>Gestiona convenios de pago para residentes morosos. Al activar un convenio, el residente quedará exento de intereses moratorios durante el periodo de activación.</p>
                    </div>
                    <button onClick={() => navigate('/administracion')} className="activar-convenios-back-link">
                        ← Regresar a administración
                    </button>
                </header>
            </div>

            <div className="search-bar">
                <div className="search-row">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar por número de casa o nombre del residente..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => handleExportReport(false)}
                        disabled={exporting || casas.filter(c => c.convenioActivo === true).length === 0}
                        className="export-button"
                    >
                        <FaDownload /> {exporting ? 'Exportando...' : 'Exportar Actual'}
                    </button>
                </div>

                <div className="search-actions">
                    <span className="search-helper-text">
                        Filtra por número de casa o nombre del residente.
                    </span>

                    <div className="historical-export-section">
                        <label className="historical-label">Exportar reporte histórico:</label>
                        <div className="date-selector-group">
                            <div className="date-input-group">
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                    disabled={exporting || loadingHistorical}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                        <option key={m} value={m}>
                                            {new Date(2024, m - 1).toLocaleDateString('es-MX', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="date-input-group">
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                    disabled={exporting || loadingHistorical}
                                >
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                                        <option key={y} value={y}>
                                            {y}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={() => handleExportReport(true)}
                                disabled={exporting || loadingHistorical}
                                className="export-historical-button"
                            >
                                <FaDownload /> {loadingHistorical ? 'Buscando...' : 'Exportar Histórico'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="convenios-list">
                {!loading && filteredCasas.length > 0 && (
                    <div className="convenios-header">
                        <span>Casa</span>
                        <span>Residente</span>
                        <span>Adeudo</span>
                        <span>Estado Convenio</span>
                        <span>Acción</span>
                    </div>
                )}

                {loading ? (
                    <p className="loading-text">Cargando residentes morosos...</p>
                ) : filteredCasas.length === 0 ? (
                    <p className="no-results-text">
                        {searchTerm
                            ? 'No se encontraron residentes morosos con ese criterio de búsqueda.'
                            : 'No hay residentes morosos en este momento.'}
                    </p>
                ) : (
                    filteredCasas.map(casa => {
                        const residentName = getResidentName(casa);
                        const adeudo = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
                        const convenioActivo = casa.convenioActivo === true;
                        const isProcessing = processingId === casa.id;

                        return (
                            <div key={casa.id} className="convenio-item">
                                <div className="convenio-inline">
                                    <div className="convenio-col">
                                        <span className="convenio-col-label">Casa</span>
                                        <span className="convenio-numero">Casa {casa.numero}</span>
                                    </div>

                                    <div className="convenio-col">
                                        <span className="convenio-col-label">Residente</span>
                                        <span className="convenio-residente">{residentName || 'Residente no registrado'}</span>
                                    </div>

                                    <div className="convenio-col">
                                        <span className="convenio-col-label">Adeudo</span>
                                        <span className={`convenio-adeudo ${adeudo > 0 ? 'adeudo-pendiente' : 'adeudo-cero'}`}>
                                            {formatCurrency(adeudo)}
                                        </span>
                                    </div>

                                    <div className="convenio-col">
                                        <span className="convenio-col-label">Estado Convenio</span>
                                        <span className={`convenio-status ${convenioActivo ? 'activo' : 'inactivo'}`}>
                                            {convenioActivo ? (
                                                <>
                                                    <FaCheckCircle style={{ marginRight: '4px' }} />
                                                    Activo
                                                </>
                                            ) : (
                                                <>
                                                    <FaTimesCircle style={{ marginRight: '4px' }} />
                                                    Inactivo
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <div className="convenio-toggle-wrapper">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={convenioActivo}
                                            onChange={() => handleToggleConvenio(casa)}
                                            disabled={isProcessing}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span className="toggle-label">
                                        {convenioActivo ? 'Desactivar' : 'Activar'}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <ConfirmationModal
                open={confirmationModal.open}
                title={confirmationModal.title}
                message={confirmationModal.message}
                type={confirmationModal.type}
                icon={confirmationModal.type === 'success' ? <FaCheckCircle /> : undefined}
                actionLabel={confirmationModal.onAction ? "Confirmar" : "Aceptar"}
                onAction={confirmationModal.onAction}
                onClose={() => setConfirmationModal({ ...confirmationModal, open: false, onAction: null })}
            />
        </div>
    );
};

export default ActivarConvenios;

